import { Button, Input, Select } from "antd";
import { TableProps } from "antd/es/table";
import dayjs from "dayjs";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ManageAnnouncement from "../../../components/Announcement/ManageAnnouncement";
import ViewManageAnnouncement from "../../../components/Announcement/ViewManageAnnouncement";
import CustomTable from "../../../components/ui/CustomTable";
import { useGetAnnouncementListQuery } from "../../../features/announcement/announcementApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { AnnouncementData, GetAnnouncementParamsType } from "../../../types";
import { countParPageSerialNumber } from "../../../utils/number";
import {
  addKeyInArray,
  selectSearchOption,
  selectStringOption,
} from "../../../utils/utils";

enum Status {
  Published = "status-success",
  Scheduled = "status-info",
  Expired = "status-dark",
}

const typeList = ["event", "holiday", "financial", "others"];
const statusList = ["scheduled", "published", "expired"];

export default function Announcement() {
  const { open, handleClose, handleOpen } = useModal();
  const { authInfo } = useAppSelector((state) => state.auth);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    keyword,
    handleSearch,
    filter,
    handleFilter,
  } = useTable<GetAnnouncementParamsType>();
  const { isLoading, isError, error, data } = useGetAnnouncementListQuery({
    params: {
      ...filter,
      search: keyword,
    },
  });
  useError(isError, error, "Get announcement data fail");

  const columns: TableProps<AnnouncementData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Type",
      dataIndex: "type",
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Created On",
      dataIndex: "start_date",
      sorter: (a, b) =>
        dayjs(a.start_date, "MMM DD, YYYY, hh:mm A").valueOf() -
        dayjs(b.start_date, "MMM DD, YYYY, hh:mm A").valueOf(),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Ends On",
      dataIndex: "end_date",
    },
    {
      title: "Applicable for",
      dataIndex: "target_type",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: keyof typeof Status) => (
        <span className={Status[value]}>{value}</span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val) => (
        <div className="flex justify-center items-center gap-2">
          {authInfo?.role === "Admin" ? (
            <Button
              className="action-button"
              size="small"
              icon={<EditIcon />}
              onClick={() =>
                handleOpen({
                  type: "UPDATE",
                  data: val,
                })
              }
            />
          ) : null}

          <Button
            className="action-button"
            size="small"
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: val,
              })
            }
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <SidebarLayout title="Announcement">
        <CustomTable
          loading={isLoading}
          limit={limit}
          page={page}
          columns={columns}
          total={data?.data?.total_announcements}
          dataSource={addKeyInArray(data?.data?.announcements)}
          handleLimit={handleLimit}
          handlePagination={handlePagination}
          // rowSelection={{
          //   type: "checkbox",
          //   onChange: (key) => setSelectedData(key as string[]),
          //   columnWidth: 40,
          // }}
          filterSection={
            <>
              <Input
                suffix={<SearchIcon />}
                placeholder="Search by title"
                onChange={handleSearch}
              />
              <Select
                allowClear
                showSearch
                onChange={(val) => handleFilter("type", val)}
                placeholder="Type"
                filterOption={selectSearchOption}
                options={selectStringOption(typeList, "string", true)}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />
              <Select
                allowClear
                showSearch
                onChange={(val) => handleFilter("status", val)}
                placeholder="Status"
                filterOption={selectSearchOption}
                options={selectStringOption(statusList, "string", true)}
                className="min-w-[130px]"
                suffixIcon={<SelectDownIcon />}
                rootClassName="icon-gray"
              />

              {authInfo?.role === "Admin" ? (
                <Button
                  type="primary"
                  className="middle-button"
                  icon={<AddIcon />}
                  onClick={() =>
                    handleOpen({
                      type: "ADD",
                    })
                  }
                >
                  Create Announcement
                </Button>
              ) : null}
            </>
          }
        />

        {(open.type === "ADD" || open.type === "UPDATE") && (
          <ManageAnnouncement
            open={open.open}
            data={open?.data}
            type={open?.type}
            handleClose={handleClose}
          />
        )}

        {open.type === "VIEW" && (
          <ViewManageAnnouncement
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
            handleEditOpen={handleOpen}
          />
        )}
      </SidebarLayout>
    </>
  );
}
